/**
 * Board row copy (#2473): every cell sub-text and both "next gate" lines of
 * one batch-board row, as a pure function of the row, the tenant flow, the
 * real today, `now` and today's ETA summary. The board only renders this.
 *
 * Rules hold for EVERY row, not only today's: `time` is the row date against
 * the real today (`dayTone` is a tag class and only knows yesterday). Every
 * clock time comes from the tenant flow (`flow.gates`); without a flow the
 * first line falls back to the wire gate label and the second stays empty.
 *
 * Row size is the sum of the six columns, never `total`: `total` counts live
 * rows only while `exception_or_cancelled` also counts cancelled tombstones
 * (#2481).
 */
import type { TFunction } from 'i18next';
import { addDays, rowSize, type BoardDay, type BoardGateKind, type BoardStage } from './boardModel';
import type { DeliveryFlowWire } from './boardWire';
import type { RouteEtaSummary } from './etaSignals';
import {
  dispatchCutoffAhead,
  EXECUTOR_LABEL,
  flowGateTime,
  flowProvider,
  hasDispatchWindow,
} from './flowCopy';
import { currentStep, flowStepModel, type FlowStepModel } from './flowSteps';
import { formatMonthDay } from './formatBatchDay';

export const GATE_LABEL = {
  packaging_cutoff: 'dispatch.board.gate.packaging_cutoff',
  dispatch_cutoff: 'dispatch.board.gate.dispatch_cutoff',
  delivery_window_end: 'dispatch.board.gate.delivery_window_end',
} as const satisfies Record<BoardGateKind, string>;

export type RowTime = 'past' | 'today' | 'future';
export type CellTone = 'danger' | 'warning' | 'muted';

export interface CellCopy {
  text: string;
  tone: CellTone;
}

export interface RowCopy {
  cells: Record<BoardStage, CellCopy | null>;
  next: { first: string; firstTone: 'default' | 'muted'; second: string };
}

export interface RowCopyInput {
  day: BoardDay;
  /** The real today (merchant calendar date), not the selected date. */
  today: string;
  now: Date;
  flow: DeliveryFlowWire | undefined;
  /** Today's route ETA summary; read only for today's row. */
  eta: RouteEtaSummary | undefined;
  /** Sold-out products for this row (selected day only); absent = 0. */
  soldOutCount?: number;
  language: string;
  t: TFunction;
}

/** The row date against the real today. */
export function rowTime(date: string, today: string): RowTime {
  if (date === today) return 'today';
  return date < today ? 'past' : 'future';
}

export type RowPhase = 'closed' | 'dispatch' | 'assign' | 'depart' | 'deliver';
export type Phase = RowPhase | 'none';

// A terminal step is never the current step; it maps to `closed` only so the
// table covers every stage.
const STEP_PHASE = {
  awaiting_dispatch: 'dispatch',
  awaiting_assignment: 'assign',
  awaiting_departure: 'depart',
  en_route: 'deliver',
  delivered: 'closed',
  exception_or_cancelled: 'closed',
} as const satisfies Record<BoardStage, RowPhase>;

/**
 * Where a row stands: the phase of its current step (#2636, decision 5),
 * skipping straggler steps. `none` = an empty batch; `null` = the flow has no
 * batch key, so no current step is computed.
 */
export function phaseOf(day: BoardDay, model: FlowStepModel, nowMs: number): Phase | null {
  const step = currentStep(model, day, nowMs);
  if (step === null || step === 'closed' || step === 'none') return step;
  return STEP_PHASE[step];
}

function clock(ms: number, timeZone: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(ms));
}

function deriveCells(
  input: RowCopyInput,
  time: RowTime,
  packaging: string | null,
): RowCopy['cells'] {
  const { day, flow, eta, t } = input;
  const c = day.counts;
  const provider = flow ? flowProvider(flow) : null;

  let dispatchCell: CellCopy | null = null;
  if (day.signals.preflightAnomalies > 0) {
    dispatchCell = {
      text: t('dispatch.board.copy.anomalies', { count: day.signals.preflightAnomalies }),
      tone: 'danger',
    };
  } else if ((input.soldOutCount ?? 0) > 0) {
    dispatchCell = {
      text: t('dispatch.board.copy.soldOut', { count: input.soldOutCount }),
      tone: 'muted',
    };
  } else if (
    time === 'today' &&
    day.nextGate?.kind === 'packaging_cutoff' &&
    c.awaiting_dispatch > 0 &&
    packaging !== null
  ) {
    dispatchCell = {
      text: t('dispatch.board.copy.afterPackaging', { at: packaging }),
      tone: 'warning',
    };
  }

  return {
    awaiting_dispatch: dispatchCell,
    awaiting_assignment:
      c.awaiting_assignment > 0 && provider !== null
        ? {
            text: t('dispatch.board.copy.assigning', { provider: t(EXECUTOR_LABEL[provider]) }),
            tone: 'warning',
          }
        : null,
    awaiting_departure:
      c.awaiting_departure > 0 ? { text: t('dispatch.board.copy.printable'), tone: 'muted' } : null,
    en_route:
      time === 'today' && eta !== undefined && eta.late > 0
        ? { text: t('dispatch.board.copy.lateDrivers', { count: eta.late }), tone: 'warning' }
        : null,
    delivered: null,
    exception_or_cancelled: null,
  };
}

function dispatchSecondToday(input: RowCopyInput, packaging: string, cutoff: string): string {
  const { day, now, t } = input;
  const gate = day.nextGate;
  if (gate?.kind === 'packaging_cutoff') {
    const minutes = Math.ceil((Date.parse(gate.atIso) - now.getTime()) / 60_000);
    // The wire gate lags the clock until the next board read: once its time
    // has come the window is open, never a zero or negative countdown.
    if (minutes <= 0) return t('dispatch.board.copy.openUntil', { at: cutoff });
    return minutes < 60
      ? t('dispatch.board.copy.opensInMinutes', { count: minutes })
      : t('dispatch.board.copy.opensAt', { at: packaging });
  }
  if (gate?.kind === 'dispatch_cutoff') return t('dispatch.board.copy.openUntil', { at: cutoff });
  return t('dispatch.board.copy.dispatchPassed');
}

export function deriveRowCopy(input: RowCopyInput): RowCopy {
  const { day, today, flow, eta, language, t } = input;
  const time = rowTime(day.date, today);
  const firstTone = time === 'today' ? 'default' : 'muted';
  const packaging = flow ? flowGateTime(flow, 'packaging_cutoff') : null;
  const cutoff = flow ? flowGateTime(flow, 'dispatch_cutoff') : null;
  const windowEnd = flow ? flowGateTime(flow, 'delivery_window_end') : null;
  // Packaging after (or with) dispatch is no span and no "dispatch after packaging" (#2631).
  const windowed = packaging !== null && cutoff !== null && hasDispatchWindow(packaging, cutoff);
  const cells = deriveCells(input, time, windowed ? packaging : null);
  const dash = { first: '—', firstTone: 'muted', second: '' } as const;

  if (flow === undefined || packaging === null || cutoff === null || windowEnd === null) {
    const gate = day.nextGate;
    return {
      cells,
      next: gate
        ? { first: t(GATE_LABEL[gate.kind], { at: gate.localTime }), firstTone, second: '' }
        : dash,
    };
  }

  const model = flowStepModel(flow);
  const provider = flowProvider(flow);
  const window = { from: packaging, to: cutoff };
  const md = formatMonthDay(day.date, language);
  const next = ((): RowCopy['next'] => {
    switch (phaseOf(day, model, input.now.getTime())) {
      case 'closed':
        return {
          first: t('dispatch.board.copy.closed'),
          firstTone,
          second: t('dispatch.board.copy.closedLate', {
            at: windowEnd,
            count: day.signals.deliveredLate,
          }),
        };
      // An empty batch still says when to dispatch: the old rule gave `dispatch` for size 0.
      case 'none':
      case 'dispatch':
        if (!windowed) {
          const dated = { date: md, at: cutoff };
          if (time === 'today') {
            return {
              first: t('dispatch.board.copy.dispatchCutoffOnly', { at: cutoff }),
              firstTone,
              second: dispatchCutoffAhead(day.nextGate, cutoff, input.now.getTime())
                ? t('dispatch.board.copy.dispatchByCutoff', { at: cutoff })
                : t('dispatch.board.copy.dispatchPassed'),
            };
          }
          if (time === 'past') {
            return rowSize(day) === 0
              ? dash
              : {
                  first: t('dispatch.board.copy.dispatchCutoffOnlyOn', dated),
                  firstTone,
                  second: t('dispatch.board.copy.dispatchPassed'),
                };
          }
          return {
            first:
              day.date === addDays(today, 1)
                ? t('dispatch.board.copy.dispatchCutoffOnlyOn', dated)
                : t('dispatch.board.copy.dispatchCutoffOnlySameDay', { at: cutoff }),
            firstTone,
            second: t('dispatch.board.copy.collecting'),
          };
        }
        if (time === 'today') {
          return {
            first: t('dispatch.board.copy.dispatchWindow', window),
            firstTone,
            second: dispatchSecondToday(input, packaging, cutoff),
          };
        }
        if (time === 'past') {
          return rowSize(day) === 0
            ? dash
            : {
                first: t('dispatch.board.copy.dispatchWindowOn', { ...window, date: md }),
                firstTone,
                second: t('dispatch.board.copy.dispatchPassed'),
              };
        }
        return {
          first:
            day.date === addDays(today, 1)
              ? t('dispatch.board.copy.dispatchWindowOn', { ...window, date: md })
              : t('dispatch.board.copy.dispatchWindowSameDay', window),
          firstTone,
          second: t('dispatch.board.copy.collecting'),
        };
      case 'assign':
        return {
          first: t('dispatch.board.copy.assignBy', { at: cutoff }),
          firstTone,
          second:
            provider === null
              ? ''
              : t('dispatch.board.copy.assignRemaining', {
                  provider: t(EXECUTOR_LABEL[provider]),
                  count: day.signals.unassignedTasks,
                }),
        };
      case 'depart':
        return {
          first: t('dispatch.board.copy.departAfter', { at: cutoff }),
          firstTone,
          second: t('dispatch.board.copy.departPrintable', {
            count: day.counts.awaiting_departure,
          }),
        };
      case 'deliver': {
        let second = '';
        if (time === 'today' && eta !== undefined) {
          if (eta.late > 0 && eta.latestLateMs !== null) {
            second = t('dispatch.board.copy.lateDriversEta', {
              count: eta.late,
              at: clock(eta.latestLateMs, flow.timezone, language),
            });
          } else if (eta.latestMs !== null) {
            second = t('dispatch.board.copy.etaDone', {
              at: clock(eta.latestMs, flow.timezone, language),
            });
          }
        }
        return { first: t('dispatch.board.copy.deliverBy', { at: windowEnd }), firstTone, second };
      }
      case null:
        return dash;
    }
  })();
  return { cells, next };
}
